/**
 * useNavFavorites — pinned favourites + auto "recently visited" for navigation
 * (FIX-NAV-FAV).
 *
 * Two surfaces share this state (module-level singletons): the sidebar renders
 * «Избранное» / «Недавние» sections, and the ⌘K palette lists favourites first.
 *
 *   • favourites — paths the user pinned (★), persisted, order = pin order;
 *   • recents    — last MAX_RECENTS distinct nav pages, newest first, auto-tracked
 *     on navigation (detail routes collapse to their nav page via resolveNavPath).
 *
 * Only KNOWN nav paths (from navModel) are pinnable / tracked, so a stale or
 * one-off URL never pollutes the lists.
 */
import { ref } from "vue";
import { ALL_NAV_ITEMS } from "./navModel";

const FAV_KEY = "cdp-nav-favorites";
const REC_KEY = "cdp-nav-recents";
const MAX_RECENTS = 6;

/** Set of every real nav-page path (so we never pin/track a random URL). */
const NAV_PATHS = new Set(ALL_NAV_ITEMS.map((i) => i.path));

function loadList(key: string): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || "[]");
    if (Array.isArray(raw)) return raw.filter((p): p is string => typeof p === "string" && NAV_PATHS.has(p));
  } catch { /* ignore corrupt storage */ }
  return [];
}
function saveList(key: string, list: string[]): void {
  try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* best-effort */ }
}

// Module-level singletons — every caller sees the same reactive lists.
const favorites = ref<string[]>(loadList(FAV_KEY));
const recents = ref<string[]>(loadList(REC_KEY));

/**
 * Collapse an arbitrary route path to the nav page it belongs to:
 * exact match wins, else the longest boundary-aware prefix (so /clients/123/edit
 * → /clients). Returns null for paths not in the nav model.
 */
export function resolveNavPath(routePath: string): string | null {
  if (typeof routePath !== "string" || routePath.length === 0) return null;
  if (NAV_PATHS.has(routePath)) return routePath;
  let best: string | null = null;
  for (const p of NAV_PATHS) {
    if (p !== "/" && routePath.startsWith(p + "/")) {
      if (best === null || p.length > best.length) best = p;
    }
  }
  return best;
}

export function useNavFavorites() {
  function isFavorite(path: string): boolean {
    return favorites.value.includes(path);
  }

  function toggleFavorite(path: string): void {
    if (!NAV_PATHS.has(path)) return;
    favorites.value = isFavorite(path)
      ? favorites.value.filter((p) => p !== path)
      : [...favorites.value, path];
    saveList(FAV_KEY, favorites.value);
  }

  /** Record a visit (newest first, deduped, capped). Detail routes collapse to their nav page. */
  function trackVisit(routePath: string): void {
    const path = resolveNavPath(routePath);
    if (!path) return;
    recents.value = [path, ...recents.value.filter((p) => p !== path)].slice(0, MAX_RECENTS);
    saveList(REC_KEY, recents.value);
  }

  return { favorites, recents, isFavorite, toggleFavorite, trackVisit };
}

/** Test seam — reset the shared lists + storage between tests. */
export function __resetNavFavoritesForTest(): void {
  favorites.value = [];
  recents.value = [];
  try {
    localStorage.removeItem(FAV_KEY);
    localStorage.removeItem(REC_KEY);
  } catch { /* ignore */ }
}
